import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import api from "../../api";

const emptyResults = {
  patients: [],
  prescribers: [],
  analyses: [],
  invitations: [],
};

const useGlobalSearch = () => {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState(emptyResults);
  const [loading, setLoading] = useState(false);
  const isMounted = useRef(true);
  const requestId = useRef(0);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchResults = useCallback(async (value) => {
    const query = (value || "").trim();
    const currentRequest = ++requestId.current;

    if (query.length < 2) {
      if (isMounted.current) {
        setResults(emptyResults);
        setLoading(false);
      }
      return;
    }

    if (isMounted.current) {
      setLoading(true);
    }

    try {
      const response = await api.fetch(
        `/V2/search?q=${encodeURIComponent(query)}&limit=5`,
      );
      const json = await response.json();

      if (!isMounted.current || currentRequest !== requestId.current) {
        return;
      }

      if (!response.ok) {
        setResults(emptyResults);
        return;
      }

      setResults({
        patients: json.patients || [],
        prescribers: json.prescribers || [],
        analyses: json.analyses || [],
        invitations: json.invitations || [],
      });
    } catch (e) {
      if (isMounted.current && currentRequest === requestId.current) {
        setResults(emptyResults);
      }
    } finally {
      if (isMounted.current && currentRequest === requestId.current) {
        setLoading(false);
      }
    }
  }, []);

  const debouncedFetch = useMemo(() => debounce(fetchResults, 400), [
    fetchResults,
  ]);

  useEffect(() => {
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch]);

  const search = useCallback(
    (value) => {
      setTerm(value);
      debouncedFetch(value);
    },
    [debouncedFetch],
  );

  const clear = useCallback(() => {
    debouncedFetch.cancel();
    setTerm("");
    setResults(emptyResults);
    setLoading(false);
  }, [debouncedFetch]);

  return { term, results, loading, search, clear };
};

export default useGlobalSearch;
